import type { ConfiguredAddOn, ConfiguredService } from '@/features/service-plans';
import type { DateKey } from '../utils/dateKey';
import { derivePlanTermMonths } from './planTerm';

/** elemis's documented `num_instalments` ceiling (contract §5). Declared ONCE,
 *  here. NOT duplicated in `PreviewScheduleRequest` (which already carries
 *  `max:1200`) or anywhere else client-side. */
export const MAX_ELEMIS_INSTALMENTS = 1200;

export type NotOfferableReason = 'below-floor' | 'above-transport-max';

/**
 * The even-monthly family's (#740) term-derivation result, a discriminated
 * union so the impossible state — "a warning AND a disabled state at once" —
 * is unrepresentable rather than a property two `if` statements happen to
 * have (D9).
 */
export type EvenMonthlyDerivation =
    /** No positively-priced dated basket item — the term cannot be worked out.
     *  Send NO `numInstalments`; elemis defaults to 12 (contract §5). SHOW THE
     *  WARNING. Never blocks the flow (product decision 3). */
    | { readonly kind: 'underivable' }
    /** A usable count within [effectiveFloor, MAX_ELEMIS_INSTALMENTS]. Send it
     *  verbatim. */
    | { readonly kind: 'offerable'; readonly numInstalments: number; readonly termMonths: number }
    /** No term could be derived, and the product configured a default: send it. */
    | { readonly kind: 'offerable-by-default'; readonly numInstalments: number }
    /** A term derived, but outside the offerable window.
     *  SLICE 1: rendered with the SAME advisory warning as `underivable` and
     *  the request falls back to elemis's 12 (see S1-W in the plan).
     *  SLICE 2 (#750): the method is rendered DISABLED with this reason.
     *  `numInstalments` is carried ONLY so the reason can name it — it is
     *  NEVER sent, and NEVER clamped up to `floor`. */
    | {
          readonly kind: 'not-offerable';
          readonly reason: NotOfferableReason;
          readonly numInstalments: number;
          /** Absent only when the count came from the default with no derivable term. */
          readonly termMonths?: number;
          readonly floor: number;
      };

/** Kept beside the union so a new variant is a compile error here,
 *  not a silently dropped count at the call site. */
export const transmittedInstalments = (derivation: EvenMonthlyDerivation): number | undefined => {
    switch (derivation.kind) {
        case 'offerable':
        case 'offerable-by-default':
            return derivation.numInstalments;
        case 'underivable':
        case 'not-offerable':
            return undefined;
        default: {
            const unreachable: never = derivation;
            return unreachable;
        }
    }
};

/** Frozen module-level singleton (in the spirit of `PaymentStep`'s existing
 *  `EMPTY_ALIGNMENT`) so the common no-term case never allocates. */
const UNDERIVABLE: EvenMonthlyDerivation = Object.freeze({ kind: 'underivable' as const });

/**
 * Turns the basket's derived plan term into the `EvenMonthlyDerivation` union
 * for the even-monthly scheduling-method family (`even_monthly`, `deposit_plus_even`).
 *
 * `leadMonths` (#751) and `minInstalments` (#750) are both supplied by
 * `PaymentStep`'s Memo A
 * (`www/src/features/quote/components/steps/PaymentStep.tsx`). Their unset
 * baseline (lead 0, floor 1) is exactly what Slice 1 ran on, and every
 * degenerate path through them is tested (`evenMonthlyTerm.test.ts`).
 *
 * THE TRAP THIS FUNCTION MUST NEVER FALL INTO: the floor is a COMPARISON,
 * never a clamp. Collecting over more months than the derived term is
 * precisely the underfunding failure #740 removes — there is no `Math.max`
 * on the transmitted count anywhere in this function.
 *
 * #767: `termMonths` here is the plan's term IN MONTHS, sent to elemis as
 * `numInstalments`. For `deposit_plus_even` specifically, elemis's shipped
 * period-0 model means one of those N instalments is spent on the deposit
 * itself (1 deposit + N−1 ordinary instalments — contract §8 B6), so
 * `deposit_plus_even` completes ONE MONTH EARLIER than `even_monthly` for the
 * same derived `termMonths`, even though this function derives an identical
 * count for both. That is deliberate and NOT fixed here: the fund completing
 * sooner is the safe direction (the exact invariant this term derivation
 * exists to protect), and the obvious correction — sending `termMonths + 1`
 * for `deposit_plus_even` — cannot be applied uniformly, because it is
 * forbidden for `item_based_with_deposit` (that method's count is pinned by
 * its final collection target, contract §8 B3; inflating it is rejected with
 * `-32602`). See the #767 plan's D8 for the full reasoning and
 * `docs/features/payment-options-and-schedule-calculation.md` (Update #767).
 * A configured default only shortens the count via `Math.min`, never stretches it.
 */
export const deriveEvenMonthlySchedule = (input: {
    services: ConfiguredService[];
    addOns: ConfiguredAddOn[];
    anchorDateKey: DateKey;
    /** `collection_lead_months`, 0 when unset. Supplied by `PaymentStep`'s
     *  Memo A (#751). */
    leadMonths?: number;
    /** `min_collection_instalments`, `undefined` when unset. SUPPLIED BY SLICE 2 (#750). */
    minInstalments?: number;
    /** `default_collection_instalments`, `undefined` when unset. */
    defaultInstalments?: number;
}): EvenMonthlyDerivation => {
    const termMonths = derivePlanTermMonths(input.services, input.addOns, input.anchorDateKey);

    // NEVER Math.max on the transmitted count — the floor is a comparison only,
    // and effectiveFloor can never fall below elemis's transport minimum of 1.
    const configuredFloor =
        typeof input.minInstalments === 'number' && Number.isInteger(input.minInstalments)
            ? input.minInstalments
            : 1;
    const floor = Math.max(1, configuredFloor);

    const configuredDefault =
        typeof input.defaultInstalments === 'number' &&
        Number.isInteger(input.defaultInstalments) &&
        input.defaultInstalments >= 1
            ? input.defaultInstalments
            : undefined;

    if (termMonths === undefined) {
        if (configuredDefault === undefined) {
            return UNDERIVABLE;
        }
        if (configuredDefault < floor) {
            return {
                kind: 'not-offerable',
                reason: 'below-floor',
                numInstalments: configuredDefault,
                floor,
            };
        }
        if (configuredDefault > MAX_ELEMIS_INSTALMENTS) {
            return {
                kind: 'not-offerable',
                reason: 'above-transport-max',
                numInstalments: configuredDefault,
                floor,
            };
        }
        return { kind: 'offerable-by-default', numInstalments: configuredDefault };
    }

    // The lead is ARITHMETIC here, not a date shift: the even-monthly family
    // sends no collection targets, so there are no dates to move (contrast
    // `applyCollectionLead`, which shifts `byDate` for the item-based family).
    // Supplied by `PaymentStep`'s Memo A (#751); 0 when the product has no
    // configured lead, or an invalid one — the fail-closed guard below.
    const lead =
        typeof input.leadMonths === 'number' &&
        Number.isInteger(input.leadMonths) &&
        input.leadMonths > 0
            ? input.leadMonths
            : 0;
    const derived = termMonths - lead;
    // Math.min is the permitted direction: a default can only shorten, never stretch.
    const numInstalments = configuredDefault === undefined ? derived : Math.min(configuredDefault, derived);

    if (numInstalments < floor) {
        return { kind: 'not-offerable', reason: 'below-floor', numInstalments, termMonths, floor };
    }
    if (numInstalments > MAX_ELEMIS_INSTALMENTS) {
        return {
            kind: 'not-offerable',
            reason: 'above-transport-max',
            numInstalments,
            termMonths,
            floor,
        };
    }
    return { kind: 'offerable', numInstalments, termMonths };
};
